import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges,
} from '@angular/core';
import { DurationFormat } from '@freelancer/pipes';
import { ButtonColor, ButtonSize } from '@freelancer/ui/button';
import { textVariants } from '@freelancer/ui/tailwind-text';
import { TextSize } from '@freelancer/ui/text';
import { MarketingMobileNumberRestrictionCodeApi } from 'api-typings/users/users';

@Component({
  standalone: false,
  selector: 'app-marketing-mobile-number-restriction',
  template: `
    <div [ngSwitch]="code">
      <div
        *ngSwitchCase="
          MarketingMobileNumberRestrictionCodeApi.USER_HAS_FIRST_WARNING_MODAL_PENALTY
        "
        flTrackingSubSection="FirstWarning"
      >
        <h1
          i18n="First Warning header"
          [class]="warningHeaderCls"
        >
          We've noticed that you might be sending quotes outside of Loadshift.
        </h1>
        <p
          class="mb-xsmall"
          i18n="First Warning quoting text"
        >
          Quoting within Loadshift guarantees secure payment upon delivery,
          while receiving reviews for completed loads can increase your chances
          of landing more jobs.
        </p>
        <p
          class="mb-xsmall"
          i18n="First Warning data"
        >
          Carriers with 5+ reviews are 3x more likely to be awarded compared to
          those with no reviews.*
        </p>
        <p
          class="text-body-16 {{
            marginBeforeCTA === 'small' ? 'mb-small' : 'mb-mid'
          }}"
          i18n="First Warning subtext"
        >
          *Based on Loadshift data from Jan 2022 to Apr 2023
        </p>
        <fl-button
          i18n="First Warning button"
          flTrackingLabel="Acknowledge"
          [color]="buttonColor"
          [display]="'block'"
          [size]="buttonSize"
          (click)="handleAgree()"
        >
          Ok, thanks
        </fl-button>
      </div>
      <div
        *ngSwitchCase="
          MarketingMobileNumberRestrictionCodeApi.USER_HAS_SECOND_WARNING_MODAL_PENALTY
        "
        flTrackingSubSection="SecondWarning"
      >
        <h1
          i18n="Second Warning header"
          [class]="warningHeaderCls"
        >
          Please submit your quotes through Loadshift.
        </h1>
        <p
          class="mb-xsmall"
          i18n="Second Warning text"
        >
          Viewing contact numbers without submitting quotes is a violation of
          terms. Your account may be restricted if you continue.
        </p>
        <p
          class="{{ marginBeforeCTA === 'small' ? 'mb-small' : 'mb-mid' }}"
          i18n="Loadshift terms and conditions declaration"
        >
          Please refer to the
          <fl-link
            flTrackingLabel="ViewTerms"
            [link]="'/about/terms'"
          >
            User Agreement
          </fl-link>
          and
          <fl-link
            flTrackingLabel="ViewCodeOfConduct"
            [link]="'/info/codeofconduct'"
          >
            Code of Conduct
          </fl-link>
          for more details.
        </p>
        <fl-button
          i18n="Second Warning button"
          flTrackingLabel="Acknowledge"
          [color]="buttonColor"
          [display]="'block'"
          [size]="buttonSize"
          (click)="handleAgree()"
        >
          I acknowledge
        </fl-button>
      </div>
      <div
        *ngSwitchDefault
        flTrackingSubSection="RestrictionPenalty"
      >
        <h1
          i18n="Restricted header"
          [class]="warningHeaderCls"
        >
          Your access to view contact details has been restricted.
        </h1>
        <p
          class="mb-xsmall"
          i18n="Restricted text"
        >
          Your account has been restricted from viewing shippers’ contact
          details as you have failed to submit quotes via Loadshift.
          @if (expiry) {
            This restriction will be lifted in
            {{ expiry | duration: DurationFormat.LONG | flAsync }}.
          }
        </p>
        <p
          i18n="Restricted text"
          class="{{
            showPenaltyButton
              ? marginBeforeCTA === 'mid'
                ? 'mb-mid'
                : 'mb-small'
              : 'mb-none'
          }}"
        >
          We encourage you to submit quotes via Loadshift to avoid this in the
          future.
        </p>
        @if (showPenaltyButton) {
          <fl-button
            i18n="Restricted button"
            flTrackingLabel="Acknowledge"
            [color]="buttonColor"
            [display]="'block'"
            [size]="buttonSize"
            (click)="handleAgree()"
          >
            I understand
          </fl-button>
        }
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MarketingMobileNumberRestrictionComponent
  implements OnInit, OnChanges
{
  DurationFormat = DurationFormat;
  MarketingMobileNumberRestrictionCodeApi =
    MarketingMobileNumberRestrictionCodeApi;

  @Input() buttonColor: ButtonColor;
  @Input() buttonSize: ButtonSize;
  @Input() code: MarketingMobileNumberRestrictionCodeApi;
  @Input() expiry?: number;
  @Input() headerSize: TextSize;
  @Input() marginBeforeCTA: 'small' | 'mid';
  @Input() showPenaltyButton: boolean;

  @Output() agreeClicked = new EventEmitter<void>();

  warningHeaderCls: string;

  ngOnInit(): void {
    this.calculateStyles();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if ('headerSize' in changes) {
      this.calculateStyles();
    }
  }

  calculateStyles(): void {
    this.warningHeaderCls = textVariants({
      class: 'mb-xsmall',
      size: this.headerSize,
    });
  }

  handleAgree(): void {
    this.agreeClicked.emit();
  }
}
